import { useEffect, useMemo, useState } from 'react'

import { Text } from '@hhgtech/hhg-components/mantine'
import { Box, BoxProps } from '@mantine/core'
import { useInterval } from '@mantine/hooks'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import useStyles from './index.styles'

type Props = {
  endDate: Date
  label?: string
  hasUnit?: boolean
  onEnd?: () => void
} & BoxProps

const formatNumber = (num: number) => {
  return String(num).length === 1 ? `0${num}` : num
}

export const CountDownTimer = ({
  endDate,
  onEnd,
  label,
  hasUnit,
  styles,
  classNames,
  className,
}: Props) => {
  const { formatMessage: f } = useIntl()
  const [seconds, setSeconds] = useState(0)
  const [isEnd, setIsEnd] = useState(false)
  const { classes, cx } = useStyles(
    { isEnd, hasUnit },
    {
      name: 'CountDownTimer',
      styles,
      classNames,
    },
  )
  const interval = useInterval(() => setSeconds((s) => s + 1), 1000)
  const dateObj = useMemo(() => {
    const date = dayjs.duration(dayjs(endDate).diff(dayjs()))
    if (Math.round(date.asSeconds()) < 1) {
      setIsEnd(true)
      onEnd?.()
      interval.stop()
      return {
        day: 0,
        hour: 0,
        minute: 0,
        second: 0,
      }
    }
    return {
      day: Math.round(date.days()),
      hour: Math.round(date.hours()),
      minute: Math.round(date.minutes()),
      second: Math.round(date.seconds()),
    }
  }, [seconds])

  useEffect(() => {
    interval.start()
    return interval.stop
  }, [])

  return (
    <Box className={cx(className, classes.root)}>
      <Text size="s4" className={classes.heading}>
        {isEnd
          ? f({
              id: 'common.ended',
            })
          : label ||
            f({
              id: 'common.startIn',
            })}
      </Text>
      <Box className={classes.listing}>
        <Box className={classes.item}>
          <span className={classes.text}>{formatNumber(dateObj.day)}</span>
          {hasUnit && (
            <span className={classes.unit}>{f({ id: 'common.day' })}</span>
          )}
        </Box>
        <Box className={classes.divider} />
        <Box className={classes.item}>
          <span className={classes.text}>{formatNumber(dateObj.hour)}</span>
          {hasUnit && (
            <span className={classes.unit}>{f({ id: 'common.hour' })}</span>
          )}
        </Box>
        <Box className={classes.divider} />
        <Box className={classes.item}>
          <span className={classes.text}>{formatNumber(dateObj.minute)}</span>
          {hasUnit && (
            <span className={classes.unit}>{f({ id: 'common.minute' })}</span>
          )}
        </Box>
        <Box className={classes.divider} />
        <Box className={classes.item}>
          <span className={classes.text}>{formatNumber(dateObj.second)}</span>
          {hasUnit && (
            <span className={classes.unit}>{f({ id: 'common.second' })}</span>
          )}
        </Box>
      </Box>
    </Box>
  )
}
